import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Menu from "./Menu";
import "./portfolio.css";
import AnsibleSimulator from "./interactive/AnsibleSimulator";
import KubernetesAutoscaler from "./interactive/KubernetesAutoscaler";
import RscPreview from "./interactive/RscPreview";
import SecurityConsole from "./interactive/SecurityConsole";
import HomelabTopology from "./interactive/HomelabTopology";
import SpotifyBuilder from "./interactive/SpotifyBuilder";
import KerasjiekPreview from "./interactive/KerasjiekPreview";
import MedievalPreview from "./interactive/MedievalPreview";

const WorkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const currentIndex = useMemo(() => Menu.findIndex((w) => String(w.id) === String(id)), [id]);
  const work = Menu[currentIndex];

  const prevWork = currentIndex > 0 ? Menu[currentIndex - 1] : Menu[Menu.length - 1];
  const nextWork = currentIndex < Menu.length - 1 ? Menu[currentIndex + 1] : Menu[0];

  const [activeView, setActiveView] = useState("interactive");
  const [isExpanded, setIsExpanded] = useState(false);

  // Smooth scroll to top when opening a new project
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActiveView(work?.interactiveType ? "interactive" : "screenshot");
    setIsExpanded(false);
  }, [id, work]);

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsExpanded(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const categoryLabel = (category) => {
    switch (category) {
      case 'Web & Apps':
        return 'Web & Apps';
      case 'DevOps & Automatisering':
        return t('devops_and_automation') && !t('devops_and_automation').includes('_')
          ? t('devops_and_automation')
          : 'DevOps & Automatisering';
      case 'Cyber Security':
        return 'Cyber Security';
      case 'Cloud & Enterprise':
        return 'Cloud & Enterprise';
      default:
        return category;
    }
  };

  if (!work) {
    return (
      <section className="work container section">
        <h2 className="section__title">Project niet gevonden</h2>
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <button className="btn" onClick={() => navigate("/#work")}>
            Terug naar overzicht
          </button>
        </div>
      </section>
    );
  }

  const renderInteractiveComponent = () => {
    switch (work.interactiveType) {
      case "ansible":
        return <AnsibleSimulator />;
      case "kubernetes":
        return <KubernetesAutoscaler />;
      case "rsc":
        return <RscPreview />;
      case "security":
        return <SecurityConsole />;
      case "homelab":
        return <HomelabTopology />;
      case "spotify":
        return <SpotifyBuilder />;
      case "kerasjiek":
        return <KerasjiekPreview />;
      case "medieval":
        return <MedievalPreview />;
      default:
        return (
          <div style={{ textAlign: "center", padding: "3rem 1rem", background: "var(--container-color)", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.08)" }}>
            <p style={{ color: "var(--text-color)", marginBottom: "1rem" }}>
              Bekijk de live website of screenshots van dit project hieronder:
            </p>
            {work.url && (
              <a
                href={work.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-action btn-action--live"
              >
                <i className="fas fa-external-link-alt"></i> Open {work.title}
              </a>
            )}
          </div>
        );
    }
  };

  return (
    <section className="work-detail-page container section">
      {/* ==================== TOP NAVIGATION BAR ==================== */}
      <div className="work-nav-bar">
        <Link to="/#work" className="work-nav-back" title="Terug naar alle projecten">
          <i className="fas fa-arrow-left"></i> <span>Projecten</span>
        </Link>

        <div className="work-nav-pager">
          <Link
            to={`/work/${prevWork.id}`}
            className="work-pager-btn"
            title={`Vorig: ${prevWork.title}`}
            aria-label="Vorig project"
          >
            <i className="fas fa-chevron-left"></i>
          </Link>
          <span className="work-pager-count">{currentIndex + 1} / {Menu.length}</span>
          <Link
            to={`/work/${nextWork.id}`}
            className="work-pager-btn"
            title={`Volgend: ${nextWork.title}`}
            aria-label="Volgend project"
          >
            <i className="fas fa-chevron-right"></i>
          </Link>
        </div>
      </div>

      {/* ==================== HERO HEADER ==================== */}
      <header className="work-hero-header">
        <div className="work-hero-meta">
          <span className="work-hero-category">{categoryLabel(work.category)}</span>
          {work.badgeText && (
            <span
              className="work-hero-badge"
              style={work.badgeColor ? { backgroundColor: work.badgeColor, color: '#fff' } : undefined}
            >
              {work.badgeText}
            </span>
          )}
        </div>

        <h1 className="work-hero-title">{t(work.title) || work.title}</h1>
        <p className="work-hero-subtitle">{work.description}</p>

        {/* Action Buttons (Only when live/github links exist) */}
        {(work.url || work.githubUrl || work.playableUrl) && (
          <div className="work-hero-actions">
            {work.url && (
              <a
                href={work.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-action btn-action--live"
              >
                <i className="fas fa-external-link-alt"></i> Live Website
              </a>
            )}
            {work.githubUrl && (
              <a
                href={work.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-action btn-action--github"
              >
                <i className="fab fa-github"></i> Broncode (GitHub)
              </a>
            )}
            {work.playableUrl && (
              <a
                href={work.playableUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-action btn-action--play"
              >
                <i className="fas fa-gamepad"></i> {work.playableLabel || 'Speel Online'}
              </a>
            )}
          </div>
        )}

        {/* Tech stack pills (Only place on the page!) */}
        {Array.isArray(work.tech) && work.tech.length > 0 && (
          <div className="work-hero-tech">
            {work.tech.map((item, idx) => (
              <span key={idx} className="work-tech-pill">
                {item}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* ==================== HERO SHOWCASE / INTERACTIVE STAGE (FULL WIDTH) ==================== */}
      <div className="work-showcase-container">
        {work.interactiveType && (
          <div className="work-showcase-tabs">
            <button
              type="button"
              className={`work-showcase-tab ${activeView === 'interactive' ? 'active' : ''}`}
              onClick={() => setActiveView('interactive')}
            >
              <i className="fas fa-bolt" style={{ color: '#facc15' }}></i> Live Demo & Simulatie
            </button>
            <button
              type="button"
              className={`work-showcase-tab ${activeView === 'screenshot' ? 'active' : ''}`}
              onClick={() => setActiveView('screenshot')}
            >
              <i className="fas fa-image"></i> Screenshot
            </button>
          </div>
        )}

        <div className="work-showcase-stage">
          {activeView === 'interactive' ? (
            <div className="work-interactive-stage">
              {renderInteractiveComponent()}
            </div>
          ) : (
            <div className="work-screenshot-stage">
              <img
                src={work.image}
                alt={work.title}
                className="work-screenshot-img"
                onClick={() => setIsExpanded(true)}
                onError={(event) => {
                  if (work.fallbackImage && event.currentTarget.src !== work.fallbackImage) {
                    event.currentTarget.src = work.fallbackImage;
                  }
                }}
              />
              <button
                type="button"
                className="work-screenshot-zoom-btn"
                onClick={() => setIsExpanded(true)}
              >
                <i className="fas fa-expand"></i> Klik om te vergroten
              </button>
            </div>
          )}
        </div>
      </div>

      {/* LIGHTBOX FOR SCREENSHOT */}
      {isExpanded && (
        <div className="work-lightbox" onClick={() => setIsExpanded(false)}>
          <div className="work-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="work-lightbox-close"
              onClick={() => setIsExpanded(false)}
              aria-label="Sluiten"
            >
              <i className="fas fa-times"></i>
            </button>
            <img src={work.image} alt={work.title} className="work-lightbox-img" />
          </div>
        </div>
      )}

      {/* ==================== WAT IK HEB GEDAAN (MIJN BIJDRAGE) ==================== */}
      {Array.isArray(work.features) && work.features.length > 0 && (
        <section className="work-contribution-section">
          <div className="work-section-heading">
            <span className="work-section-badge">
              <i className="fas fa-user-check"></i> Mijn Rol & Bijdrage
            </span>
            <h2 className="work-section-title">Wat ik heb gedaan</h2>
            <p className="work-section-subtitle">Concrete werkzaamheden en onderdelen die ik in dit project heb gerealiseerd:</p>
          </div>

          <div className="work-features-grid">
            {work.features.map((feat, idx) => (
              <div key={idx} className="work-feature-tile">
                <div className="work-feature-tile-header">
                  <div className="work-feature-icon">
                    <i className={feat.icon || "fas fa-check"}></i>
                  </div>
                  <h3 className="work-feature-title">{feat.title}</h3>
                </div>
                <p className="work-feature-desc">{feat.desc}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== CONTEXT (DOEL & RESULTAAT) ==================== */}
      {(work.problem || work.solution) && (
        <div className="work-ps-grid">
          {work.problem && (
            <div className="work-ps-card">
              <span className="work-ps-label work-ps-label--problem">
                <i className="fas fa-bullseye"></i> Doel & Vraagstuk
              </span>
              <p className="work-ps-text">{work.problem}</p>
            </div>
          )}
          {work.solution && (
            <div className="work-ps-card">
              <span className="work-ps-label work-ps-label--solution">
                <i className="fas fa-check-circle"></i> Oplossing & Resultaat
              </span>
              <p className="work-ps-text">{work.solution}</p>
            </div>
          )}
        </div>
      )}

      {/* ==================== BOTTOM NAVIGATION FOOTER ==================== */}
      <footer className="work-bottom-nav">
        <div className="work-nav-prev-next">
          <Link to={`/work/${prevWork.id}`} className="work-pn-card work-pn-card--prev">
            <span className="work-pn-label"><i className="fas fa-arrow-left"></i> Vorig Project</span>
            <span className="work-pn-title">{prevWork.title}</span>
            <span className="work-pn-category">{categoryLabel(prevWork.category)}</span>
          </Link>

          <Link to={`/work/${nextWork.id}`} className="work-pn-card work-pn-card--next">
            <span className="work-pn-label">Volgend Project <i className="fas fa-arrow-right"></i></span>
            <span className="work-pn-title">{nextWork.title}</span>
            <span className="work-pn-category">{categoryLabel(nextWork.category)}</span>
          </Link>
        </div>

        <div className="work-return-center">
          <Link to="/#work" className="btn btn--return">
            <i className="fas fa-th-large"></i> Terug naar Alle Projecten
          </Link>
        </div>
      </footer>
    </section>
  );
};

export default WorkDetail;
